/**
 * PetronilleAiOverlayHook
 *
 * Attached to the `.petronille-ai` overlay element. The overlay is teleported
 * into `<body>` via Phoenix's `<.portal>` and is conditionally rendered (`:if`),
 * so the hook's `mounted()` fires when the overlay opens and `destroyed()`
 * fires when it closes (or when the page navigates away).
 *
 * Responsibilities (RGAA 7.1 / WCAG 2.1.2):
 * - Lock background DOM via the `inert` attribute so Tab and clicks cannot
 *   reach elements behind the overlay.
 * - Move focus into the overlay on open and restore it on close.
 * - Bind Escape to the existing close path (which dispatches `refresh-page`).
 */

const hook = {
  mounted() {
    this.previouslyFocused = document.activeElement;
    this.markedInert = [];

    // Mark every direct child of <body> as inert except the subtree containing
    // this overlay (the portal wrapper). Skip elements that were already inert
    // so we don't clear them on teardown.
    for (const child of Array.from(document.body.children)) {
      if (!child.contains(this.el) && !child.inert) {
        child.inert = true;
        this.markedInert.push(child);
      }
    }

    // Move focus into the overlay so Tab cycles within it from the start.
    const firstFocusable = this.el.querySelector(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();

    // Escape closes — dispatch the same event as the close button does
    // (handled by PetronilleAiHook to reload the page).
    this.escapeHandler = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        this.el.dispatchEvent(new Event("refresh-page", { bubbles: true }));
        window.dispatchEvent(new Event("refresh-page"));
      }
    };
    this.el.addEventListener("keydown", this.escapeHandler);
  },

  destroyed() {
    for (const child of this.markedInert) child.inert = false;
    this.markedInert = [];

    // Restore focus to whatever opened the overlay so keyboard users don't
    // land on <body>.
    if (this.previouslyFocused && document.body.contains(this.previouslyFocused)) {
      this.previouslyFocused.focus();
    }

    if (this.escapeHandler) {
      this.el.removeEventListener("keydown", this.escapeHandler);
    }
  },
};

export default hook;
